'use client';

import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { ScrollArea } from './ui/scroll-area';

export interface TocItem {
  id: string;
  text: string;
  level: number;
}

// 从 Markdown 内容中提取标题
export function extractHeadings(markdown: string): TocItem[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const headings: TocItem[] = [];
  let match;

  while ((match = headingRegex.exec(markdown)) !== null) {
    if (!match[1] || !match[2]) continue;

    const level = match[1].length;
    const text = match[2].trim();
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
      .replace(/^-+|-+$/g, '');

    headings.push({ id, text, level });
  }

  return headings;
}

interface ArticleTocProps {
  content: string;
  className?: string;
}

export function ArticleToc({ content, className }: ArticleTocProps) {
  const [activeId, setActiveId] = useState<string>('');
  const headings = extractHeadings(content);
  const activeItemRef = useRef<HTMLLIElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings.map((h) =>
        document.getElementById(h.id),
      );
      const scrollPosition = window.scrollY + 100;

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const element = headingElements[i];
        const heading = headings[i];
        if (element && heading && element.offsetTop <= scrollPosition) {
          setActiveId(heading.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  // 当 activeId 变化时，滚动到激活的目录项
  useEffect(() => {
    if (activeItemRef.current && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (scrollContainer) {
        const itemTop = activeItemRef.current.offsetTop;
        const itemHeight = activeItemRef.current.offsetHeight;
        const containerHeight = scrollContainer.clientHeight;
        const scrollTop = scrollContainer.scrollTop;

        // 如果目录项不在可视区域内，则滚动到该项
        if (
          itemTop < scrollTop ||
          itemTop + itemHeight > scrollTop + containerHeight
        ) {
          scrollContainer.scrollTo({
            top: itemTop - containerHeight / 2 + itemHeight / 2,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [activeId]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);

    if (element) {
      const offsetTop = element.offsetTop - 8;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <nav className={cn('space-y-1', className)}>
      <h3 className="font-semibold text-sm mb-4">本页内容</h3>
      <ScrollArea
        className="h-60 w-full bg-background p-4 rounded-lg"
        ref={scrollAreaRef}
      >
        <ul className="space-y-2">
          {headings.map((heading) => (
            <li
              key={heading.id}
              ref={activeId === heading.id ? activeItemRef : null}
              style={{ paddingLeft: `${(heading.level - 2) * 12}px` }}
            >
              <button
                onClick={() => handleClick(heading.id)}
                className={cn(
                  'block w-full text-left text-sm transition-colors hover:text-foreground text-muted-foreground',
                  activeId === heading.id
                    ? 'text-primary font-medium'
                    : 'text-muted-foreground',
                )}
              >
                {heading.text}
              </button>
            </li>
          ))}
        </ul>
      </ScrollArea>
    </nav>
  );
}
